<template>
  <div class="wallet-card">
    <button
      class="wallet-card__wrapper"
      data-testid="wallet-card"
      @click="emitClick"
      @mouseenter="handleEnter"
      @mouseleave="handleLeave"
    >
      <div class="wallet-card__base">
        <ProgressiveImage
          v-if="hasImage"
          class="wallet-card__base-image"
          :placeholder="image"
          :image="image"
        />
        <div v-else class="wallet-card__base-placeholder" />
        <div class="wallet-card__base-content">
          <ChipContext
            :label="hasHover ? 'Gestão Warren' : ''"
            icon="G0010"
            context="brand"
            :class="{
              'wallet-card__base-chip-context': true,
              'wallet-card__base-chip-context--hover': hasHover
            }"
          />
          <div>
            <BaseText
              size="p4"
              class="wallet-card__base-title"
              :color="NebraskaColors.textOverImage"
            >
              {{ title }}
            </BaseText>
            <BaseText size="p5" :color="NebraskaColors.elementAlpha">
              {{ subtitle }}
            </BaseText>
          </div>
        </div>
      </div>
    </button>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { BaseText, ChipContext } from '@warrenbrasil/nebraska-web';
import { ProgressiveImage } from '@/modules/common/components/ProgressiveImage';

interface EmitEvents {
  (event: 'click'): void;
}
const emit = defineEmits<EmitEvents>();

interface Props {
  image: string;
  title: string;
  subtitle: string;
}
const props = defineProps<Props>();

const hasHover = ref(false);

const hasImage = computed(() => {
  return props.image;
});

function handleEnter() {
  hasHover.value = true;
}

function handleLeave() {
  hasHover.value = false;
}

function emitClick() {
  emit('click');
}
</script>

<style lang="less" scoped>
button {
  width: 100%;
  padding: 0;
}

.wallet-card {
  &__wrapper {
    display: block;
    height: 100%;
    position: relative;
    transform: translate3d(0, 0, 1px);
    z-index: 10;
  }

  &__base {
    position: relative;
    padding: @size-spacing-x500;
    border-radius: @size-radius-x400;
    min-height: 260px;
    min-width: 100%;
    max-width: 384px;
    text-align: left;
    display: flex;
    flex-direction: column;

    &-title {
      margin-bottom: @size-spacing-x150;
      font-weight: bold;
    }

    &-chip-context {
      align-self: flex-start;
      padding: @size-spacing-x150 @size-spacing-x150;
      color: @status-brand;

      :deep(.chip--icon) {
        margin-right: 0;
      }

      &--hover :deep(.chip--icon) {
        margin-right: @size-spacing-x150;
      }

      :deep(svg) {
        height: 18px !important;
        width: auto !important;
      }
    }

    &-content {
      position: relative;
      z-index: 2;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      flex-grow: 1;
    }

    &-placeholder {
      position: absolute;
      z-index: 1;
      border-radius: inherit;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: @background-overlay;
    }

    &-image {
      position: absolute;
      z-index: 1;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      &::before {
        content: '';
        position: absolute;
        z-index: 2;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          180deg,
          rgba(51, 49, 49, 0) 13%,
          rgba(51, 49, 49, 0.75) 75%
        );
      }
    }

    &:hover :deep(img) {
      filter: blur(3px);
      transform: scale(1.1);
    }
  }
}
</style>
